/**
 * Pre-analyzed playback — drives `AudioFeatures` from a `.analysis.json`
 * timeline rather than realtime FFT. Sample-accurate sync.
 *
 * JSON schema (matches `app/songs/*.analysis.json` and `viz/cli/analyze.py`):
 *   {
 *     duration, sample_rate, bpm,
 *     beats: number[],            // beat times (s)
 *     energy_fps: number,
 *     energy_times: number[],     // sample times (s)
 *     bass: number[],             // 0..1 per sample
 *     mid: number[],
 *     treble: number[],
 *   }
 */

import { AudioFeatures, FeatureProvider, ZERO_FEATURES } from "./types";

export interface AnalysisJson {
  duration: number;
  sample_rate?: number;
  bpm: number;
  beats: number[];
  downbeats?: number[];
  energy_fps?: number;
  energy_times: number[];
  bass: number[];
  mid: number[];
  treble: number[];
}

export class PreanalyzedProvider implements FeatureProvider {
  private snapshot: AudioFeatures = { ...ZERO_FEATURES };
  private nextBeatIdx = 0;
  private beatEnv = 0;
  private readonly beatDecayPerMs = 0.0025;
  private lastTickMs = performance.now();
  private rafId: number | null = null;
  private beatCount = 0;

  constructor(
    private readonly audio: HTMLAudioElement,
    private readonly data: AnalysisJson,
  ) {
    this.tick = this.tick.bind(this);
    this.rafId = requestAnimationFrame(this.tick);
  }

  /** Linear interpolation lookup over an evenly-or-irregularly sampled curve. */
  private sampleCurve(times: number[], values: number[], t: number): number {
    if (times.length === 0) return 0;
    if (t <= times[0]) return values[0] ?? 0;
    if (t >= times[times.length - 1]) return values[values.length - 1] ?? 0;
    // Binary search
    let lo = 0;
    let hi = times.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (times[mid] <= t) lo = mid;
      else hi = mid;
    }
    const t0 = times[lo];
    const t1 = times[hi];
    const v0 = values[lo] ?? 0;
    const v1 = values[hi] ?? 0;
    const span = t1 - t0;
    if (span <= 0) return v0;
    const frac = (t - t0) / span;
    return v0 + (v1 - v0) * frac;
  }

  private tick(): void {
    this.rafId = requestAnimationFrame(this.tick);

    const now = performance.now();
    const dt = now - this.lastTickMs;
    this.lastTickMs = now;

    const t = this.audio.currentTime;
    const playing = !this.audio.paused && !this.audio.ended;

    // Reset beat search when user seeks backward.
    if (this.nextBeatIdx > 0 && this.data.beats[this.nextBeatIdx - 1] > t + 0.05) {
      this.nextBeatIdx = 0;
      this.beatCount = 0;
    }

    // Fire all beats whose timestamp we've crossed since last frame.
    if (playing) {
      while (
        this.nextBeatIdx < this.data.beats.length &&
        this.data.beats[this.nextBeatIdx] <= t
      ) {
        this.beatEnv = 1;
        this.beatCount++;
        this.nextBeatIdx++;
      }
    }

    this.beatEnv = Math.max(0, this.beatEnv - this.beatDecayPerMs * dt);

    const bass = playing
      ? this.sampleCurve(this.data.energy_times, this.data.bass, t)
      : 0;
    const mid = playing
      ? this.sampleCurve(this.data.energy_times, this.data.mid, t)
      : 0;
    const treble = playing
      ? this.sampleCurve(this.data.energy_times, this.data.treble, t)
      : 0;

    this.snapshot = {
      bass,
      mid,
      treble,
      level: (bass + mid + treble) / 3,
      bpm: this.data.bpm ?? 0,
      beat: this.beatEnv,
      beatCount: this.beatCount,
      t: now,
    };
  }

  read(): AudioFeatures {
    return this.snapshot;
  }

  stop(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }
}
